import { useCallback, useRef, useState } from "react";

/**
 * Returns a callback ref (not a ref object) on purpose: the observed node
 * often mounts later than the component calling the hook (e.g. only after a
 * query resolves), and an effect-attached observer would never see it. The
 * callback attaches/detaches the observer exactly when the node does.
 */
export function useInView<T extends Element>(threshold = 0) {
  const [isInView, setIsInView] = useState(false);
  // node's top edge has scrolled above the viewport
  const [isAbove, setIsAbove] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const ref = useCallback(
    (node: T | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;

      if (!node || typeof IntersectionObserver === "undefined") {
        setIsInView(false);
        setIsAbove(false);
        return;
      }

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry) {
            return;
          }
          setIsInView(entry.isIntersecting);
          setIsAbove(
            !entry.isIntersecting &&
              entry.boundingClientRect.top < (entry.rootBounds?.top ?? 0),
          );
        },
        { threshold },
      );
      observer.observe(node);
      observerRef.current = observer;
    },
    [threshold],
  );

  return { ref, isInView, isAbove };
}
